import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * UsersPage - User Management
 *
 * Configuration page for managing user records with
 * bulk import/export and CRUD operations.
 */
import { useState, useCallback } from 'react';
import { ActionIcon, Badge, Box, Button, Group, Menu, Modal, MultiSelect, NumberInput, Stack, Table, Text, TextInput, Tooltip, } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { useUsers, useImportUsers, useCreateUser, useUpdateUser, useDeleteUser, useDepartments } from '@commander/api-client';
import { IconPlus, IconSearch, IconUpload, IconDownload, IconEdit, IconTrash } from '@/components/icons';
import { BulkImportModal } from '@/components/BulkImportModal';
import { PanelHeader } from '@/components/PanelHeader';
/* ====================================
   CSS Keyframe Animations (injected once)
   Premium animations for table rows and page entrance.
   ==================================== */
const usersKeyframes = `
  @keyframes usersRowSlideIn {
    from {
      opacity: 0;
      transform: translateY(6px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes usersPageFadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;
// Inject keyframes into document head (only once)
if (typeof document !== 'undefined') {
    const styleId = 'users-keyframes';
    if (!document.getElementById(styleId)) {
        const style = document.createElement('style');
        style.id = styleId;
        style.textContent = usersKeyframes;
        document.head.appendChild(style);
    }
}
const USER_COLUMNS = ['cardNumber', 'username', 'departmentIds', 'maxCheckoutDurationHours', 'maxConcurrentItems'];
const USER_SAMPLE_DATA = [
    { cardnumber: 'CARD-001', username: 'jsmith', departmentids: 'ops,security', maxcheckoutdurationhours: '8', maxconcurrentitems: '3' },
    { cardnumber: 'CARD-002', username: 'mjones', departmentids: 'ops', maxcheckoutdurationhours: '4', maxconcurrentitems: '2' },
];
export function UsersPage() {
    const [search, setSearch] = useState('');
    const [debouncedSearch] = useDebouncedValue(search, 300);
    const [importModalOpen, setImportModalOpen] = useState(false);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [newUser, setNewUser] = useState({
        cardNumber: '',
        username: '',
        departmentIds: [],
        maxCheckoutDurationHours: 8,
        maxConcurrentItems: 3,
    });
    const [editTarget, setEditTarget] = useState(null);
    const [editForm, setEditForm] = useState({
        cardNumber: '',
        username: '',
        departmentIds: [],
        maxCheckoutDurationHours: 8,
        maxConcurrentItems: 3,
    });
    const [deleteTarget, setDeleteTarget] = useState(null);
    const { data, isLoading } = useUsers({ search: debouncedSearch });
    const { data: departments } = useDepartments();
    const importUsers = useImportUsers();
    const createUser = useCreateUser();
    const updateUser = useUpdateUser();
    const deleteUser = useDeleteUser();
    const handleImport = useCallback(async (file) => {
        const result = await importUsers.mutateAsync(file);
        return result;
    }, [importUsers]);
    const handleCreate = useCallback(async () => {
        try {
            await createUser.mutateAsync(newUser);
            notifications.show({
                title: 'User Created',
                message: `Successfully created user ${newUser.username}`,
                color: 'green',
            });
            setCreateModalOpen(false);
            setNewUser({ cardNumber: '', username: '', departmentIds: [], maxCheckoutDurationHours: 8, maxConcurrentItems: 3 });
        }
        catch {
            notifications.show({
                title: 'Error',
                message: 'Failed to create user',
                color: 'red',
            });
        }
    }, [createUser, newUser]);
    const openEdit = useCallback((user) => {
        setEditForm({
            cardNumber: user.cardNumber,
            username: user.username,
            departmentIds: user.departmentIds,
            maxCheckoutDurationHours: user.maxCheckoutDurationHours,
            maxConcurrentItems: user.maxConcurrentItems,
        });
        setEditTarget(user);
    }, []);
    const handleUpdate = useCallback(async () => {
        if (!editTarget)
            return;
        try {
            await updateUser.mutateAsync({ id: editTarget.id, ...editForm });
            notifications.show({ title: 'User Updated', message: `Updated ${editForm.username}`, color: 'green' });
            setEditTarget(null);
        }
        catch {
            notifications.show({ title: 'Error', message: 'Failed to update user', color: 'red' });
        }
    }, [updateUser, editTarget, editForm]);
    const handleDelete = useCallback(async () => {
        if (!deleteTarget)
            return;
        try {
            await deleteUser.mutateAsync(deleteTarget.id);
            notifications.show({ title: 'User Deleted', message: `Deleted ${deleteTarget.username}`, color: 'green' });
            setDeleteTarget(null);
        }
        catch {
            notifications.show({ title: 'Error', message: 'Failed to delete user', color: 'red' });
        }
    }, [deleteUser, deleteTarget]);
    const handleExport = useCallback(() => {
        if (!data?.items.length) {
            notifications.show({
                title: 'No Data',
                message: 'No users to export',
                color: 'yellow',
            });
            return;
        }
        const headers = ['cardNumber', 'username', 'departmentIds', 'maxCheckoutDurationHours', 'maxConcurrentItems', 'createdAt', 'lastLoginAt'];
        const csv = [
            headers.join(','),
            ...data.items.map((item) => headers.map((h) => {
                const value = item[h];
                if (Array.isArray(value))
                    return value.join(';');
                return String(value || '');
            }).join(',')),
        ].join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `users-export-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        notifications.show({
            title: 'Export Complete',
            message: `Exported ${data.items.length} user records`,
            color: 'green',
        });
    }, [data]);
    return (_jsxs(Stack, { gap: "lg", style: { animation: 'usersPageFadeIn 400ms ease-out' }, children: [_jsxs(Box, { children: [_jsx(Text, { style: {
                            fontSize: '1.75rem',
                            fontWeight: 500,
                            letterSpacing: '-0.01em',
                            color: 'var(--text-primary)',
                        }, children: "User Directory" }), _jsx(Text, { style: {
                            fontSize: '0.875rem',
                            fontWeight: 400,
                            color: 'var(--text-muted)',
                            marginTop: 4,
                        }, children: "Manage personnel and their equipment access permissions" })] }), _jsxs(Box, { style: {
                    backgroundColor: 'var(--bg-card)',
                    border: '1px solid var(--border-colour)',
                    borderRadius: 6,
                    overflow: 'hidden',
                }, children: [_jsx(PanelHeader, { title: "Personnel Registry", accentColour: "#1e293b", children: _jsxs(Group, { gap: "sm", children: [_jsxs(Menu, { shadow: "md", width: 200, children: [_jsx(Menu.Target, { children: _jsx(Button, { variant: "subtle", size: "xs", color: "gray", leftSection: _jsx(IconDownload, { size: 14 }), children: "Export" }) }), _jsx(Menu.Dropdown, { children: _jsx(Menu.Item, { onClick: handleExport, children: "Export to CSV" }) })] }), _jsx(Button, { variant: "subtle", size: "xs", color: "gray", leftSection: _jsx(IconUpload, { size: 14 }), onClick: () => setImportModalOpen(true), children: "Import" }), _jsx(Button, { variant: "light", size: "xs", leftSection: _jsx(IconPlus, { size: 14 }), color: "blue", onClick: () => setCreateModalOpen(true), children: "Add User" })] }) }), _jsx(Box, { style: {
                            padding: '1rem',
                            borderBottom: '1px solid var(--border-colour)',
                            background: 'var(--bg-base)',
                        }, children: _jsx(TextInput, { placeholder: "Search by username or card number...", leftSection: _jsx(IconSearch, { size: 16, style: { color: 'var(--text-muted)' } }), value: search, onChange: (e) => setSearch(e.target.value), styles: {
                                input: {
                                    backgroundColor: 'var(--bg-card)',
                                    border: '1px solid var(--border-colour)',
                                    color: 'var(--text-primary)',
                                    '&::placeholder': { color: 'var(--text-muted)' },
                                },
                            }, style: { maxWidth: 400 } }) }), _jsxs(Box, { style: { padding: '1rem' }, children: [_jsxs(Table, { styles: {
                                    table: { borderCollapse: 'separate', borderSpacing: 0 },
                                    th: {
                                        fontSize: '0.7rem',
                                        fontWeight: 500,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.08em',
                                        color: 'var(--text-muted)',
                                        padding: '0.75rem 1rem',
                                        borderBottom: '1px solid var(--border-colour)',
                                        background: 'var(--bg-base)',
                                    },
                                    td: {
                                        padding: '0.75rem 1rem',
                                        borderBottom: '1px solid var(--border-colour)',
                                        color: 'var(--text-primary)',
                                        transition: 'background 150ms ease',
                                    },
                                    tr: {
                                        '&:hover td': {
                                            background: 'var(--bg-base)',
                                        },
                                    },
                                }, children: [_jsx(Table.Thead, { children: _jsxs(Table.Tr, { children: [_jsx(Table.Th, { children: "Username" }), _jsx(Table.Th, { children: "Card Number" }), _jsx(Table.Th, { children: "Departments" }), _jsx(Table.Th, { children: "Max Duration" }), _jsx(Table.Th, { children: "Max Items" }), _jsx(Table.Th, { children: "Last Login" }), _jsx(Table.Th, { style: { width: 100 }, children: "Actions" })] }) }), _jsx(Table.Tbody, { children: isLoading ? (_jsx(Table.Tr, { children: _jsx(Table.Td, { colSpan: 7, children: _jsx(Text, { ta: "center", py: "xl", size: "sm", c: "dimmed", children: "Loading..." }) }) })) : data?.items.length === 0 ? (_jsx(Table.Tr, { children: _jsx(Table.Td, { colSpan: 7, children: _jsx(Text, { ta: "center", py: "xl", c: "dimmed", children: "No users found." }) }) })) : (data?.items.map((user, rowIndex) => (_jsxs(Table.Tr, { style: {
                                                /* Premium: staggered row entrance with fade-in and slide-up */
                                                animation: `usersRowSlideIn 350ms ease-out ${rowIndex * 40}ms backwards`,
                                                /* Premium: left border accent that appears on hover via box-shadow inset */
                                                transition: 'box-shadow 200ms ease, background-color 150ms ease',
                                                boxShadow: 'inset 0 0 0 0 transparent',
                                            }, onMouseEnter: (e) => {
                                                e.currentTarget.style.boxShadow = 'inset 3px 0 0 0 #3B82F6';
                                            }, onMouseLeave: (e) => {
                                                e.currentTarget.style.boxShadow = 'inset 0 0 0 0 transparent';
                                            }, children: [_jsx(Table.Td, { children: _jsx(Text, { fw: 400, style: { color: 'var(--text-primary)' }, children: user.username }) }), _jsx(Table.Td, { children: _jsx(Text, { ff: "monospace", fw: 400, size: "sm", c: "dimmed", children: user.cardNumber }) }), _jsx(Table.Td, { children: _jsxs(Badge, { variant: "light", size: "sm", color: "blue", children: [user.departmentIds.length, " dept", user.departmentIds.length !== 1 ? 's' : ''] }) }), _jsx(Table.Td, { children: _jsxs(Text, { ff: "monospace", size: "sm", c: "dimmed", children: [user.maxCheckoutDurationHours, "h"] }) }), _jsx(Table.Td, { children: _jsx(Text, { ff: "monospace", size: "sm", c: "dimmed", children: user.maxConcurrentItems }) }), _jsx(Table.Td, { children: _jsx(Text, { size: "sm", ff: "monospace", c: "dimmed", children: user.lastLoginAt
                                                            ? new Date(user.lastLoginAt).toLocaleString()
                                                            : '—' }) }), _jsx(Table.Td, { children: _jsxs(Group, { gap: 4, children: [_jsx(Tooltip, { label: "Edit", children: _jsx(ActionIcon, { variant: "subtle", size: "sm", color: "gray", onClick: () => openEdit(user), children: _jsx(IconEdit, { size: 14 }) }) }), _jsx(Tooltip, { label: "Delete", children: _jsx(ActionIcon, { variant: "subtle", size: "sm", color: "red", onClick: () => setDeleteTarget(user), children: _jsx(IconTrash, { size: 14 }) }) })] }) })] }, user.id)))) })] }), data?.items.length ? (_jsx(Box, { style: {
                                    marginTop: '1rem',
                                    paddingTop: '0.75rem',
                                    borderTop: '1px solid var(--border-colour)',
                                }, children: _jsxs(Text, { size: "xs", c: "dimmed", ff: "monospace", children: ["Showing ", data.items.length, " of ", data.total, " records"] }) })) : null] })] }), _jsx(BulkImportModal, { opened: importModalOpen, onClose: () => setImportModalOpen(false), title: "Import Users", entityType: "users", expectedColumns: USER_COLUMNS, onImport: handleImport, sampleData: USER_SAMPLE_DATA }), _jsx(Modal, { opened: !!editTarget, onClose: () => setEditTarget(null), title: "Edit User", styles: {
                    header: { backgroundColor: 'var(--bg-card)', borderBottom: '1px solid var(--border-colour)' },
                    body: { backgroundColor: 'var(--bg-base)' },
                    title: { fontWeight: 600, color: 'var(--text-primary)' },
                    content: { backgroundColor: 'var(--bg-base)', border: '1px solid var(--border-colour)' },
                }, children: _jsxs(Stack, { gap: "md", children: [_jsx(TextInput, { label: "Username", required: true, value: editForm.username, onChange: (e) => setEditForm({ ...editForm, username: e.target.value }), styles: {
                                label: { color: 'var(--text-primary)', marginBottom: 4 },
                                input: { backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-colour)', color: 'var(--text-primary)' },
                            } }), _jsx(TextInput, { label: "Card Number", required: true, value: editForm.cardNumber, onChange: (e) => setEditForm({ ...editForm, cardNumber: e.target.value }), styles: {
                                label: { color: 'var(--text-primary)', marginBottom: 4 },
                                input: { backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-colour)', color: 'var(--text-primary)' },
                            } }), _jsx(MultiSelect, { label: "Departments", data: departments?.items.map(d => ({ value: d.id, label: d.name })) || [], value: editForm.departmentIds, onChange: (value) => setEditForm({ ...editForm, departmentIds: value }), styles: {
                                label: { color: 'var(--text-primary)', marginBottom: 4 },
                                input: { backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-colour)', color: 'var(--text-primary)' },
                            } }), _jsx(NumberInput, { label: "Max Checkout Duration (hours)", min: 1, max: 168, value: editForm.maxCheckoutDurationHours, onChange: (value) => setEditForm({ ...editForm, maxCheckoutDurationHours: Number(value) || 8 }), styles: {
                                label: { color: 'var(--text-primary)', marginBottom: 4 },
                                input: { backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-colour)', color: 'var(--text-primary)' },
                            } }), _jsx(NumberInput, { label: "Max Concurrent Items", min: 1, max: 20, value: editForm.maxConcurrentItems, onChange: (value) => setEditForm({ ...editForm, maxConcurrentItems: Number(value) || 3 }), styles: {
                                label: { color: 'var(--text-primary)', marginBottom: 4 },
                                input: { backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-colour)', color: 'var(--text-primary)' },
                            } }), _jsxs(Group, { justify: "flex-end", mt: "md", children: [_jsx(Button, { variant: "subtle", color: "gray", onClick: () => setEditTarget(null), children: "Cancel" }), _jsx(Button, { color: "blue", onClick: handleUpdate, loading: updateUser.isPending, children: "Save Changes" })] })] }) }), _jsx(Modal, { opened: !!deleteTarget, onClose: () => setDeleteTarget(null), title: "Delete User", styles: {
                    header: { backgroundColor: 'var(--bg-card)', borderBottom: '1px solid var(--border-colour)' },
                    body: { backgroundColor: 'var(--bg-base)' },
                    title: { fontWeight: 600, color: 'var(--text-primary)' },
                    content: { backgroundColor: 'var(--bg-base)', border: '1px solid var(--border-colour)' },
                }, children: _jsxs(Stack, { gap: "md", children: [_jsxs(Text, { size: "sm", style: { color: 'var(--text-primary)' }, children: ["Are you sure you want to delete user ", _jsx("strong", { children: deleteTarget?.username }), " (", deleteTarget?.cardNumber, ")? This action cannot be undone."] }), _jsxs(Group, { justify: "flex-end", children: [_jsx(Button, { variant: "subtle", color: "gray", onClick: () => setDeleteTarget(null), children: "Cancel" }), _jsx(Button, { color: "red", onClick: handleDelete, loading: deleteUser.isPending, children: "Delete User" })] })] }) }), _jsx(Modal, { opened: createModalOpen, onClose: () => setCreateModalOpen(false), title: "Add User", styles: {
                    header: { backgroundColor: 'var(--bg-card)', borderBottom: '1px solid var(--border-colour)' },
                    body: { backgroundColor: 'var(--bg-base)' },
                    title: { fontWeight: 600, color: 'var(--text-primary)' },
                    content: { backgroundColor: 'var(--bg-base)', border: '1px solid var(--border-colour)' },
                }, children: _jsxs(Stack, { gap: "md", children: [_jsx(TextInput, { label: "Username", placeholder: "jsmith", required: true, value: newUser.username, onChange: (e) => setNewUser({ ...newUser, username: e.target.value }), styles: {
                                label: { color: 'var(--text-primary)', marginBottom: 4 },
                                input: { backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-colour)', color: 'var(--text-primary)' },
                            } }), _jsx(TextInput, { label: "Card Number", placeholder: "CARD-001", required: true, value: newUser.cardNumber, onChange: (e) => setNewUser({ ...newUser, cardNumber: e.target.value }), styles: {
                                label: { color: 'var(--text-primary)', marginBottom: 4 },
                                input: { backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-colour)', color: 'var(--text-primary)' },
                            } }), _jsx(MultiSelect, { label: "Departments", placeholder: "Select departments", data: departments?.items.map(d => ({ value: d.id, label: d.name })) || [], value: newUser.departmentIds, onChange: (value) => setNewUser({ ...newUser, departmentIds: value }), styles: {
                                label: { color: 'var(--text-primary)', marginBottom: 4 },
                                input: { backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-colour)', color: 'var(--text-primary)' },
                            } }), _jsx(NumberInput, { label: "Max Checkout Duration (hours)", placeholder: "8", min: 1, max: 168, value: newUser.maxCheckoutDurationHours, onChange: (value) => setNewUser({ ...newUser, maxCheckoutDurationHours: Number(value) || 8 }), styles: {
                                label: { color: 'var(--text-primary)', marginBottom: 4 },
                                input: { backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-colour)', color: 'var(--text-primary)' },
                            } }), _jsx(NumberInput, { label: "Max Concurrent Items", placeholder: "3", min: 1, max: 20, value: newUser.maxConcurrentItems, onChange: (value) => setNewUser({ ...newUser, maxConcurrentItems: Number(value) || 3 }), styles: {
                                label: { color: 'var(--text-primary)', marginBottom: 4 },
                                input: { backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-colour)', color: 'var(--text-primary)' },
                            } }), _jsxs(Group, { justify: "flex-end", mt: "md", children: [_jsx(Button, { variant: "subtle", color: "gray", onClick: () => setCreateModalOpen(false), children: "Cancel" }), _jsx(Button, { color: "blue", onClick: handleCreate, loading: createUser.isPending, disabled: !newUser.username || !newUser.cardNumber, children: "Create User" })] })] }) })] }));
}
//# sourceMappingURL=UsersPage.js.map